import React, {
  useCallback,
  useEffect, useMemo, useState,
} from 'react';
import useLocale from '@/hooks/useLocale';
import {
  IconCheckBg, IconCardRefresh, IconExternalLink, Modal, RadioCollapse,
  IconTimeIllustrated,
  IconAtencion,
  IconLoading,
  Skeleton,
} from 'gatorcomponents';
import * as Styles from './usePaymentModal.styles';
import { useResize } from '@/hooks/useResize';
import { useDispatch, useSelector } from 'react-redux';
import {
  PAYMENT_MODAL_CALLER, PAYMENT_STATUS, PaymentMethodsTypes, PaymentStatusTypes, PaypalActionTypes,
  CALLER,
} from './usePaymentModal.types';
import { PaymentStatus } from './components/PaymentStatus';
import { DefaultPaymentAction } from './components/DefaultPaymentAction';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { Ticket } from './components/Ticket';
import { TicketDialog } from './components/TicketDialog';
import { billingActions, commonActions, invoicesActions } from '@/redux/modules';
import PaypalDialog from '@/components/Paypal/PaypalDialog';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import {
  CHARLIE_NEW_PAYMENT_MODAL_OPTIONS, XRAY_ENABLED_SPLIT_PAYMENT, XRAY_LIMIT_TIME_IN_SECONDS_TO_DISPLAY_PIX_CODE, XRAY_REQUEST_PAYMENT_METHODS_BY_PRODUCT,
} from '@/config/GrowthBook/constants';
import PropTypes from 'prop-types';
import { useDiagnostic } from '@/components/Layout/Menu/MyAlerts';
import { billingAnalytics } from '@/analytics';
import { InvoiceInfos } from './components/InvoiceInfos';
import { InvoiceDetails } from './components/InvoiceDetails';
import { NewCreditCard } from './components/NewCreditCard';
import { getCreditCardInstallments, getCreditCardInstallmentsByProductData, loadCreditCard } from '@/redux/actions/creditCard';
import { CurrentCreditCard } from './components/CurrentCreditCard';
import { isBRServer } from '@/utils/Validators/validation';
import { useCreditCardPayment } from '../useCreditCardPayment';
import { useHistory, useParams } from 'react-router';
import { payTicketInvoiceForES, checkBoletoUserData } from '@/redux/actions/invoices';
import {
  ERROR_CHANGE_PAYMENT_METHOD, ERROR_PAYMENT_METHOD_TICKET_INVOICE_ES, RECEIVE_PAYMENT_METHOD_TICKET_INVOICE_ES, SUCCESS_CHANGE_PAYMENT_METHOD,
} from '@/redux/actions/actionsTypes';
import { locale } from '@/utils/locale';
import { changePaymentMethodOption } from '@/redux/actions/paymentMethod';
import { PixContent } from './components/PixContent';
import { SplitPaymentModal } from '@/components/Invoices/SplitPaymentModal';

const usePaymentModal = ({
  onClose,
  subscriptionData,
  invoiceData,
  invoiceId,
  caller,
  handleUpgradeSitelock,
  paymentFromInvoicesPage,
}) => {
  const calledFromInvoices = caller === CALLER.INVOICES;
  const calledFromSubscriptions = caller === CALLER.SUBSCRIPTIONS;
  const [showInvoiceDetails, setShowInvoiceDetails] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [selectedPaymentMethodOptions, setSelectedPaymentMethodOptions] = useState(null);
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState(null);
  const [selectedMethodKey, setSelectedMethodKey] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [ticketData, setTicketData] = useState(null);
  const [loadingPrintTicket, setLoadingPrintTicket] = useState(false);
  const [loadingCreditCardInstallments, setLoadingCreditCardInstallments] = useState(true);
  const [creditCardInstallments, setCreditCardInstallments] = useState([]);
  const [installment, setInstallment] = useState(null);
  const [useOnNextRenovation, setUseOnNextRenovation] = useState(false);
  const [loadingInvoiceData, setLoadingInvoiceData] = useState(false);
  const [lastPaymentMethodExternalUrls, setLastPaymentMethodExternalUrls] = useState(null);
  const [isCcButtonEnabled, setCcButtonEnabled] = useState(true);
  const [isCcButtonLoading, setCcButtonLoading] = useState(false);
  const [isPaymentSplited, setPaymentSplited] = useState(false);

  const currentCreditCard = useSelector(state => state.creditCard);
  const invoicesState = useSelector(state => state.invoicesV2);
  const oldInvoicesState = useSelector(state => state.invoices);
  const {
    subscription,
    paymentMethodsByInvoice,
    paymentMethodsBySubscription,
    paymentMethodsV2: paymentMethodsState,
  } = useSelector(state => state.billing);

  const enabledSplitPayment = useFeatureIsOn(XRAY_ENABLED_SPLIT_PAYMENT);
  const newPaymentModalOptions = useFeatureValue(CHARLIE_NEW_PAYMENT_MODAL_OPTIONS);
  const enabledTimeToDisplayPixCode = useFeatureValue(XRAY_LIMIT_TIME_IN_SECONDS_TO_DISPLAY_PIX_CODE);
  const limitTimeToDisplayPixCode = useFeatureValue(XRAY_LIMIT_TIME_IN_SECONDS_TO_DISPLAY_PIX_CODE);
  const enabledGetPaymentMethodsByProduct = useFeatureValue(XRAY_REQUEST_PAYMENT_METHODS_BY_PRODUCT);

  const params = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { requestDiagnostic } = useDiagnostic();

  const { subscriptions: subscriptionAnalytics } = billingAnalytics;
  const { switchPaymentMethod } = subscription;
  const { loading: loadingSwitchPaymentMethod } = switchPaymentMethod;

  const limitTimeInSecondsToDisplayPixCode = enabledTimeToDisplayPixCode && limitTimeToDisplayPixCode.time_in_secounds != null
    ? limitTimeToDisplayPixCode && limitTimeToDisplayPixCode.time_in_secounds
    : null;

  const existsCreditCard = !!currentCreditCard && !!currentCreditCard.cardLastFour;
  const isCreditCard = selectedPaymentMethod && (
    selectedPaymentMethod.includes(PaymentMethodsTypes.BRASPAG_CREDIT)
    || selectedPaymentMethod === PaymentMethodsTypes.CURRENT_CREDIT_CARD.toLowerCase()
    || selectedPaymentMethod === PaymentMethodsTypes.DLOCAL);

  const isCcMethod = methodKey => methodKey === 'dlocal' || methodKey.includes('braspag_credit');

  const hideCurrentCcMethod = calledFromSubscriptions
    && subscriptionData
    && subscriptionData.paymentMethod
    && isCcMethod(subscriptionData.paymentMethod);

  const canDisplayInvoiceDetails = !!invoicesState.invoiceDetails.data && !invoicesState.invoiceDetails.loading;

  const loadingPaymentMethods = caller === CALLER.INVOICES ? paymentMethodsByInvoice.loading : paymentMethodsBySubscription.loading;
  const paymentMethodsByProduct = caller === CALLER.INVOICES ? paymentMethodsByInvoice.methods : paymentMethodsBySubscription.methods;

  const resetStates = useCallback(() => {
    setCcButtonLoading(false);
    setCcButtonEnabled(true);
    setShowInvoiceDetails(false);
    setSelectedPaymentMethod(null);
    setPaymentStatus(null);
    setSelectedMethodKey(null);
    setPaymentSplited(false);
    dispatch(invoicesActions.invoices.loadDetailsV2.resetState());
    dispatch(billingActions.paymentMethods.byInvoice.reset());
    dispatch(billingActions.paymentMethods.bySubscription.reset());
    dispatch(invoicesActions.invoices.generatePix.setPix({
      qrCode: null,
      code: null,
    }));
  }, [dispatch]);

  const handleClose = useCallback(() => {
    if (!isCcButtonLoading) {
      resetStates();
      onClose && onClose();
    }
  }, [resetStates, onClose, isCcButtonLoading]);

  const getInvoiceData = () => {
    if (invoiceData) {
      return invoiceData;
    }
    if (invoicesState.invoiceDetails.data) {
      return invoicesState.invoiceDetails.data.invoice;
    }
    if (invoiceId) {
      const item = (oldInvoicesState.byStatus.Unpaid.find(invoice => invoice.id === invoiceId));

      return item;
    }
    return invoicesState.invoiceData;
  };

  const invoice = getInvoiceData();

  const getPaymentMethods = useCallback(() => {
    if (enabledGetPaymentMethodsByProduct) {
      if (calledFromInvoices) {
        return paymentMethodsByInvoice.methods;
      }
      if (calledFromSubscriptions) {
        return paymentMethodsBySubscription.methods;
      }
    }

    return paymentMethodsState.methods;
  }, [
    calledFromInvoices,
    calledFromSubscriptions,
    enabledGetPaymentMethodsByProduct,
    paymentMethodsByInvoice.methods,
    paymentMethodsBySubscription.methods,
    paymentMethodsState.methods,
  ]);

  const paymentMethods = getPaymentMethods();

  const displaySplitInvoice = paymentFromInvoicesPage
    && invoice
    && invoice.isSplittable
    && enabledSplitPayment
    && !isPaymentSplited;

  const {
    usePaymentModal: paymentModalLocale,
    manageSubscriptionPaymentInvoiceModal: manageSubscriptionPaymentInvoiceModalLocale,
    routes: routesLocale,
  } = useLocale();

  const windowRect = useResize();
  const { width } = windowRect;
  const mobile = width < 960;

  const setPaymentStatusPix = useCallback(() => {
    if (invoicesState && invoicesState.pixFailure) {
      setPaymentStatus(PaymentStatusTypes.GENERIC_FAILURE);
    }

    if (invoicesState && invoicesState.qrCode && !invoicesState.pixFailure) {
      setPaymentStatus(PaymentStatusTypes.PIX_ONGOING);
    }
  }, [invoicesState]);

  useEffect(() => {
    setPaymentStatusPix();
  }, [setPaymentStatusPix]);

  const clearPix = useCallback(() => {
    dispatch(invoicesActions.invoices.generatePix.setPix({
      qrCode: null,
      pixCode: null,
    }));
  }, [dispatch]);

  useCallback(() => {
    clearPix();
  }, [clearPix]);

  const getPaymentMethod = useCallback(() => {
    const selectedOption = selectedPaymentMethodOptions && selectedPaymentMethodOptions.find(option => option.code === selectedPaymentMethodCode);
    const paymentMethod = selectedOption && selectedOption.provider;

    if (isCreditCard && installment) {
      return installment && installment.value;
    }

    if (selectedPaymentMethod && !selectedOption) {
      return selectedPaymentMethod;
    }

    return paymentMethod;
  }, [
    installment,
    selectedPaymentMethod,
    selectedPaymentMethodOptions,
    selectedPaymentMethodCode,
    isCreditCard,
  ]);

  const handleSuccessSwitchPaymentMethod = useCallback((bool) => {
    if (bool) {
      onClose && onClose();
      resetStates();

      if (isCreditCard) {
        dispatch(loadCreditCard());
      }
    }
  }, [onClose, isCreditCard, resetStates, dispatch]);

  const handleSwitchPaymentMethod = useCallback(({ method } = {}) => {
    let paymentMethod = null;

    if (method) {
      paymentMethod = method;
    }

    if (!method) {
      paymentMethod = getPaymentMethod() || (installment && installment.value);
    }

    const toast = {
      ...paymentModalLocale.successChangeMethodToast,
      showIcon: 'true',
    };

    dispatch(billingActions.subscriptions.switchPaymentMethod.request(
      {
        subscription: {
          id: params.subscriptionId,
          type: params.subscriptionType,
        },
        paymentMethod,
        onSuccess: handleSuccessSwitchPaymentMethod,
        usePaymentModalV2: true,
        method: selectedPaymentMethodCode || selectedPaymentMethod,
        toast,
      },
    ));
  }, [
    params,
    dispatch,
    selectedPaymentMethodCode,
    selectedPaymentMethod,
    handleSuccessSwitchPaymentMethod,
    paymentModalLocale.successChangeMethodToast,
    installment,
    getPaymentMethod,
  ]);

  const {
    canSubmit,
    renderIframe,
    sendCurrentCreditCard,
    submitCreditCard,
  } = useCreditCardPayment({
    creditCardInstallments,
    selectedInstallment: installment,
    setCcButtonEnabled,
    setCcButtonLoading,
    setPaymentStatus,
    handleSwitchPaymentMethod,
    useOnNextRenovation,
    invoiceId: invoice && invoice.id,
    caller,
  });

  const handleSeeDetails = () => {
    if (!invoicesState.invoiceDetails.data && invoice && invoice.id) {
      dispatch(invoicesActions.invoices.loadDetailsV2.request({ invoiceId: invoice.id }));
    }
    setShowInvoiceDetails(!showInvoiceDetails);
  };

  const requestPaymentMethods = useCallback(() => {
    const requestPaymentMethodsByProduct = () => {
      if (calledFromSubscriptions) {
        const serviceType = params.subscriptionType;
        const serviceId = params.subscriptionId;

        dispatch(billingActions.paymentMethods.bySubscription.request({ serviceType, serviceId }));
      }

      if (calledFromInvoices && invoice) {
        dispatch(billingActions.paymentMethods.byInvoice.request({ invoiceId: invoice.id }));
      }
    };

    if (enabledGetPaymentMethodsByProduct) {
      requestPaymentMethodsByProduct();
    }

    if (!enabledGetPaymentMethodsByProduct) {
      dispatch(billingActions.paymentMethods.request());
    }
  }, [
    params.subscriptionId,
    params.subscriptionType,
    enabledGetPaymentMethodsByProduct,
    calledFromSubscriptions,
    calledFromInvoices,
    invoice,
    dispatch,
  ]);

  useEffect(() => {
    requestPaymentMethods();
  }, [requestPaymentMethods]);

  const loadBaseRequests = useCallback(() => {
    const needLoadInvoiceDetails = invoiceId && (!invoice || invoice.id !== invoiceId);

    if (needLoadInvoiceDetails) {
      dispatch(invoicesActions.invoices.loadDetailsV2.request({ invoiceId }));
    }

    if (existsCreditCard) {
      setSelectedMethodKey(PaymentMethodsTypes.CURRENT_CREDIT_CARD);
    }

    if (!existsCreditCard) {
      dispatch(loadCreditCard()).then((data) => {
        if (data && data.creditCard && data.creditCard.cardlastfour) {
          setSelectedMethodKey(PaymentMethodsTypes.CURRENT_CREDIT_CARD);
        }
      });
    }

    if (hideCurrentCcMethod) {
      setSelectedMethodKey(null);
    }
  }, [
    dispatch,
    invoice,
    invoiceId,
    existsCreditCard,
    hideCurrentCcMethod,
  ]);

  useEffect(() => {
    loadBaseRequests();
  }, [loadBaseRequests]);

  const mountCreditCardInstallments = useCallback(() => {
    if (((invoice && invoice.id)) || params.subscriptionId) {
      setLoadingCreditCardInstallments(true);

      if (enabledGetPaymentMethodsByProduct && paymentMethodsByProduct) {
        const filterCcMethod = Object.entries(paymentMethodsByProduct).find(
          ([key]) => key === 'dlocal' || key === 'braspag_credit',
        );

        const ccMethod = filterCcMethod && filterCcMethod[1];

        setLoadingCreditCardInstallments(false);
        ccMethod && setCreditCardInstallments(ccMethod[0].installments);
      }

      if (!enabledGetPaymentMethodsByProduct) {
        if (params.subscriptionId) {
          dispatch(getCreditCardInstallmentsByProductData(params.subscriptionId, params.subscriptionType)).then((data) => {
            setLoadingCreditCardInstallments(false);
            setCreditCardInstallments(data.installments);
          });
        }

        if (invoice && invoice.id) {
          dispatch(getCreditCardInstallments(invoice.id)).then((data) => {
            setLoadingCreditCardInstallments(false);
            setCreditCardInstallments(data.installments);
          });
        }
      }
    }
  }, [
    dispatch,
    enabledGetPaymentMethodsByProduct,
    invoice,
    params.subscriptionId,
    params.subscriptionType,
    paymentMethodsByProduct,
  ]);

  useEffect(() => {
    mountCreditCardInstallments();
  }, [mountCreditCardInstallments]);

  const handleSelectPaymentMethod = useCallback(({ key, options, method }) => {
    if (!isCcButtonLoading) {
      const paymentMethod = method.provider || method.key;

      setSelectedMethodKey(key);
      setSelectedPaymentMethod(paymentMethod);
      setSelectedPaymentMethodOptions(options);

      if (!options) {
        setSelectedPaymentMethodCode(method.code);
      }

      if (options && options.length === 1) {
        setSelectedPaymentMethodCode(options[0].code);
      }
    }
  }, [isCcButtonLoading]);

  const goToTicketPrint = () => {
    window.open(ticketData.url, '_blank');
    setTicketData(null);
    handleClose();
  };

  const getPaypalDialogProps = useCallback(() => {
    const defaultProps = {
      open: true,
      enabledChangePayPalAccount: newPaymentModalOptions.payPal,
      invoiceId: invoice && invoice.id,
      showResumeData: false,
      enabledChangeAccount: true,
      close: handleClose,
      isSubscriptionsPage: false,
      labelsLocale: manageSubscriptionPaymentInvoiceModalLocale,
      insidePaymentModalV2: true,
      switchPaymentMethod: calledFromInvoices
        ? null
        : () => handleSwitchPaymentMethod({ method: PaymentMethodsTypes.GP_PAYPAL }),
    };

    const getCallingOrigin = () => {
      const rules = {
        [PAYMENT_MODAL_CALLER.INVOICES_MANAGER]: PaypalActionTypes.CALLING_BY_MANAGER_INVOICES,
        [PAYMENT_MODAL_CALLER.SUBSCRIPTION_LIST]: PaypalActionTypes.CALLING_BY_LIST_SUBSCRIPTIONS,
        [CALLER.SUBSCRIPTIONS]: PaypalActionTypes.CALLING_BY_PAYMENT_MODAL,
        [CALLER.SITELOCK]: PaypalActionTypes.CALLING_BY_PAYMENT_MODAL,
      };

      return rules[caller] || PaypalActionTypes.CALLING_BY_MANAGER_INVOICES;
    };

    const onPaymentPayPalExecuted = (event) => {
      if (event && event.status === PAYMENT_STATUS.SUCCESS) {
        if (caller === PAYMENT_MODAL_CALLER.SITELOCK_UPGRADE) {
          handleUpgradeSitelock && handleUpgradeSitelock();
        }
        requestDiagnostic('paymentModal1');
        if (calledFromSubscriptions) {
          dispatch(billingActions.subscriptions.request());
        }
        setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
      }

      if (event && event.status === PAYMENT_STATUS.FAIL) {
        setPaymentStatus(PaymentStatusTypes.ERROR_GENERIC);
      }

      const rules = {
        [PAYMENT_MODAL_CALLER.SUBSCRIPTION_MANAGER]: subscriptionAnalytics.executePaymentClick('3_pago_paypal'),
        [PAYMENT_MODAL_CALLER.SITELOCK_UPGRADE]: subscriptionAnalytics.executePaymentClick('3_pago_paypal'),
        [PAYMENT_MODAL_CALLER.INVOICES_MANAGER]: subscriptionAnalytics.executePaymentInvoiceManagerClick('3_pago_paypal'),
        [PAYMENT_MODAL_CALLER.SUBSCRIPTION_LIST]: subscriptionAnalytics.executePaymentInSubscriptionsListClick('3_pago_paypal'),
      };

      return rules[caller];
    };

    return {
      ...defaultProps,
      onPaymentExecuted: onPaymentPayPalExecuted,
      callingOrigin: getCallingOrigin(),
    };
  }, [
    manageSubscriptionPaymentInvoiceModalLocale,
    handleClose,
    dispatch,
    invoice,
    handleUpgradeSitelock,
    requestDiagnostic,
    subscriptionAnalytics,
    newPaymentModalOptions.payPal,
    handleSwitchPaymentMethod,
    calledFromSubscriptions,
    caller,
    calledFromInvoices,
  ]);

  const getTicketProps = () => {
    const buttonLabel = calledFromSubscriptions
      ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.GP_BOLETO].changeMethodButtonLabel
      : paymentModalLocale.paymentMethods[PaymentMethodsTypes.GP_BOLETO].getTicketButton;

    return {
      ...paymentModalLocale.paymentMethods[PaymentMethodsTypes.GP_BOLETO],
      setTicketData,
      handleSwitchPaymentMethod,
      loadingSwitchPaymentMethod,
      caller,
      invoice,
      buttonLabel,
    };
  };

  const loadInvoiceData = useCallback(() => {
    setLoadingInvoiceData(true);

    if (!invoice || !invoice.id) {
      return null;
    }

    const requestParams = {
      method: getPaymentMethod(),
      invoiceId: invoice.id,
      bank: selectedPaymentMethodCode,
    };

    dispatch(changePaymentMethodOption(requestParams)).then((response) => {
      if (response.type === SUCCESS_CHANGE_PAYMENT_METHOD) {
        dispatch(payTicketInvoiceForES(invoice.id)).then((response) => {
          if (response.type === RECEIVE_PAYMENT_METHOD_TICKET_INVOICE_ES) {
            window.open(response.url, '_blank', 'popup');
            setLastPaymentMethodExternalUrls(response.url);
            setPaymentStatus(PaymentStatusTypes.FOLLOW_NEW_TAB_INSTRUCTIONS);
          }

          if (response.type === ERROR_PAYMENT_METHOD_TICKET_INVOICE_ES) {
            dispatch(commonActions.notifications.set({
              label: locale('notifyFailMessage'),
              type: 'error',
            }));
          }
          setLoadingInvoiceData(false);
        });
      }

      if (response.type === ERROR_CHANGE_PAYMENT_METHOD) {
        dispatch(commonActions.notifications.set({
          label: locale('notifyFailMessage'),
          type: 'error',
        }));

        setLoadingInvoiceData(false);
      }
    });
  }, [
    dispatch,
    invoice,
    selectedPaymentMethodCode,
    getPaymentMethod,
  ]);

  const handleClickOption = useCallback(() => {
    if (caller === CALLER.INVOICES) {
      loadInvoiceData();
    }

    if (caller === CALLER.SUBSCRIPTIONS) {
      handleSwitchPaymentMethod();
    }
  }, [
    caller,
    loadInvoiceData,
    handleSwitchPaymentMethod,
  ]);

  const getQrInteroperableProps = useCallback((paymentMethodList) => {
    const handleClick = () => {
      handleClickOption();
    };

    return {
      label: calledFromInvoices ? paymentModalLocale.paymentMethods.defaultContinue : paymentModalLocale.paymentMethods.defaultContinueChangeMethod,
      description: paymentModalLocale.paymentMethods.defaultDescription,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
      onClick: handleClick,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
    };
  }, [
    loadingInvoiceData,
    handleClickOption,
    loadingSwitchPaymentMethod,
    calledFromInvoices,
    paymentModalLocale,
  ]);

  const getBilleteraElectronicaProps = useCallback((paymentMethodList) => {
    const handleClick = () => {
      handleClickOption();
    };

    return {
      label: calledFromInvoices ? paymentModalLocale.paymentMethods.defaultContinue : paymentModalLocale.paymentMethods.defaultContinueChangeMethod,
      description: paymentModalLocale.paymentMethods.defaultDescription,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
      onClick: handleClick,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
    };
  }, [
    loadingInvoiceData,
    handleClickOption,
    loadingSwitchPaymentMethod,
    calledFromInvoices,
    paymentModalLocale,
  ]);

  const installmentsOptions = useMemo(() => {
    if (creditCardInstallments && creditCardInstallments.length > 0) {
      const getInstallmentsNumber = installment => (enabledGetPaymentMethodsByProduct ? installment.number : installment.installments);
      const getInstallmentValue = installment => (enabledGetPaymentMethodsByProduct ? installment.value : installment.installmentValue);

      return creditCardInstallments.map(installment => ({
        label: paymentModalLocale.installmentLabel(getInstallmentsNumber(installment), formatCurrency(getInstallmentValue(installment))),
        value: installment.gateway,
      }));
    }

    return [];
  }, [creditCardInstallments, paymentModalLocale, enabledGetPaymentMethodsByProduct]);

  const handleSubmitCreditCard = (isNewCc) => {
    isNewCc ? submitCreditCard() : sendCurrentCreditCard();
  };

  const newCreditCardProps = () => {
    const defaultProps = {
      invoice,
      setPaymentStatus,
      setInstallment,
      setUseOnNextRenovation,
      loadingCreditCardInstallments,
      creditCardInstallments,
      installmentsOptions,
      existsCreditCard,
      isButtonLoading: isCcButtonLoading || loadingSwitchPaymentMethod,
      isCcButtonEnabled,
      canSubmit,
      renderIframe,
      submitCreditCard: () => handleSubmitCreditCard(true),
    };

    if (isBRServer) {
      return {
        ...paymentModalLocale.paymentMethods[PaymentMethodsTypes.BRASPAG_CREDIT],
        ...defaultProps,
        saveCcButtonLabel: calledFromInvoices
          ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.BRASPAG_CREDIT].saveCcButtonLabel
          : paymentModalLocale.paymentMethods[PaymentMethodsTypes.BRASPAG_CREDIT].changeMethodButtonLabel,
      };
    }

    if (!isBRServer) {
      return {
        ...paymentModalLocale.paymentMethods[PaymentMethodsTypes.DLOCAL],
        ...defaultProps,
        saveCcButtonLabel: calledFromInvoices
          ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.DLOCAL].saveCcButtonLabel
          : paymentModalLocale.paymentMethods[PaymentMethodsTypes.DLOCAL].changeMethodButtonLabel,
      };
    }
  };

  const currentCreditCardProps = () => ({
    ...paymentModalLocale.paymentMethods[PaymentMethodsTypes.CURRENT_CREDIT_CARD],
    payInvoiceButtonLabel: calledFromInvoices
      ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.CURRENT_CREDIT_CARD].payInvoiceButtonLabel
      : paymentModalLocale.paymentMethods[PaymentMethodsTypes.CURRENT_CREDIT_CARD].changeMethodButtonLabel,
    invoice,
    setPaymentStatus,
    setInstallment,
    setUseOnNextRenovation,
    isButtonLoading: isCcButtonLoading || loadingSwitchPaymentMethod,
    submitCurrentCreditCard: () => handleSubmitCreditCard(false),
    loadingCreditCardInstallments,
    creditCardInstallments,
    installmentsOptions,
    currentCreditCardData: currentCreditCard,
  });

  const getBankTransferProps = useCallback((paymentMethodList) => {
    const handleClick = () => {
      handleClickOption();
    };

    return {
      label: calledFromInvoices ? paymentModalLocale.paymentMethods.defaultContinue : paymentModalLocale.paymentMethods.defaultContinueChangeMethod,
      description: paymentModalLocale.paymentMethods.defaultDescription,
      onClick: handleClick,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
      multiplePaymentMethodsDescription: paymentModalLocale.paymentMethods.multiplePaymentMethodsDescription,
    };
  }, [
    paymentModalLocale,
    loadingInvoiceData,
    handleClickOption,
    loadingSwitchPaymentMethod,
    calledFromInvoices,
  ]);

  const getEfectivePaymentProps = useCallback((paymentMethodList) => {
    const onClick = () => {
      calledFromInvoices
        ? loadInvoiceData()
        : handleSwitchPaymentMethod();
    };

    return ({
      label: calledFromInvoices
        ? paymentModalLocale.paymentMethods.defaultContinue
        : paymentModalLocale.paymentMethods.defaultContinueChangeMethod,
      description: paymentModalLocale.paymentMethods.defaultDescription,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
      onClick,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
    });
  }, [
    loadingSwitchPaymentMethod,
    paymentModalLocale,
    loadingInvoiceData,
    loadInvoiceData,
    calledFromInvoices,
    handleSwitchPaymentMethod,
  ]);

  const handleRequestPixQrCode = useCallback((
    isExpiredButton = false,
  ) => {
    if (isExpiredButton && sessionStorage.getItem('infoPix')) {
      const infoPix = JSON.parse(sessionStorage.getItem('infoPix'));
      if (!infoPix.failure) {
        dispatch(invoicesActions.invoices.generatePix.setPix({
          qrCode: infoPix.qrCode,
          code: infoPix.code,
          failure: false,
        }));
        dispatch(invoicesActions.invoices.generatePix.success());
        setLoadingInvoiceData(false);
        return;
      }
    }

    if (calledFromInvoices) {
      dispatch(
        invoicesActions.invoices.generatePix.request({
          invoiceId,
          callBackFuction: () => setLoadingInvoiceData(false),
        }),
      );
    }
  }, [
    dispatch,
    invoiceId,
    calledFromInvoices,
  ]);

  const getPayuBoletoProps = useCallback((paymentMethodList) => {
    const onClick = () => {
      calledFromInvoices
        ? loadInvoiceData()
        : handleSwitchPaymentMethod();
    };

    return ({
      label: calledFromInvoices
        ? paymentModalLocale.paymentMethods.defaultContinue
        : paymentModalLocale.paymentMethods.defaultContinueChangeMethod,
      description: paymentModalLocale.paymentMethods.defaultDescription,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
      onClick,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
    });
  }, [
    loadingSwitchPaymentMethod,
    paymentModalLocale,
    loadingInvoiceData,
    loadInvoiceData,
    calledFromInvoices,
    handleSwitchPaymentMethod,
  ]);

  const getPixProps = useMemo(() => (paymentMethodList) => {
    const onClick = () => {
      setLoadingInvoiceData(true);

      if (calledFromInvoices) {
        handleRequestPixQrCode();
      }

      if (calledFromSubscriptions) {
        handleSwitchPaymentMethod();
      }
    };

    return {
      label: calledFromInvoices
        ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.PIX].label
        : paymentModalLocale.paymentMethods[PaymentMethodsTypes.PIX].labelChangePaymentMethod,
      description: calledFromInvoices
        ? paymentModalLocale.paymentMethods[PaymentMethodsTypes.PIX].description
        : null,
      loading: loadingInvoiceData || loadingSwitchPaymentMethod,
      onClick,
      selectedPaymentMethodOptions: paymentMethodList,
      setSelectedPaymentMethodCode,
      qrCode: invoicesState.qrCode,
      code: invoicesState.pixCode,
      securePixTransation: paymentModalLocale.paymentMethods[PaymentMethodsTypes.PIX].secureTransition,
    };
  }, [
    invoicesState,
    loadingInvoiceData,
    loadingSwitchPaymentMethod,
    calledFromInvoices,
    paymentModalLocale,
    calledFromSubscriptions,
    handleSwitchPaymentMethod,
    handleRequestPixQrCode,
  ]);

  const paymentMethodsRules = (method, paymentMethodList = []) => ({
    [PaymentMethodsTypes.GP_BOLETO]: newPaymentModalOptions.gpBoleto && <Ticket {...getTicketProps()} />,
    [PaymentMethodsTypes.GP_PAYPAL]: newPaymentModalOptions.payPal && <PaypalDialog {...getPaypalDialogProps()} />,
    [PaymentMethodsTypes.BRASPAG_CREDIT]: newPaymentModalOptions.creditCard && <NewCreditCard {...newCreditCardProps()} />,
    [PaymentMethodsTypes.DLOCAL]: newPaymentModalOptions.creditCard && <NewCreditCard {...newCreditCardProps()} />,
    [PaymentMethodsTypes.CURRENT_CREDIT_CARD]: newPaymentModalOptions.creditCard && <CurrentCreditCard {...currentCreditCardProps()} />,

    [PaymentMethodsTypes.QR_PAYMENT]: newPaymentModalOptions.qrPayment && <DefaultPaymentAction {...getQrInteroperableProps(paymentMethodList)} />,
    [PaymentMethodsTypes.WALLET]: newPaymentModalOptions.wallet && <DefaultPaymentAction {...getBilleteraElectronicaProps(paymentMethodList)} />,
    [PaymentMethodsTypes.BANK_TRANSFER]: newPaymentModalOptions.bankTransfer && <DefaultPaymentAction {...getBankTransferProps(paymentMethodList)} />,
    [PaymentMethodsTypes.EFECTIVE_PAYMENT]: newPaymentModalOptions.cashPayment && <DefaultPaymentAction {...getEfectivePaymentProps(paymentMethodList)} />,
    [PaymentMethodsTypes.PAYU_BOLETO]: newPaymentModalOptions.cashPayment && <DefaultPaymentAction {...getPayuBoletoProps(paymentMethodList)} />,
    [PaymentMethodsTypes.PIX]: newPaymentModalOptions.pix && <DefaultPaymentAction {...getPixProps(paymentMethodList)} />,
  })[method];

  const mountPaymentMethodObject = (method) => {
    if (!newPaymentModalOptions.creditCard) {
      delete paymentMethods.braspag_credit;
      delete paymentMethods.dlocal;
    }

    if (!newPaymentModalOptions.payPal) {
      delete paymentMethods.paypal;
      delete paymentMethods.gp_paypal;
    }

    if (!newPaymentModalOptions.qrPayment) {
      delete paymentMethods.qr_payment;
    }

    if (!newPaymentModalOptions.wallet) {
      delete paymentMethods.wallet;
    }

    if (!newPaymentModalOptions.bankTransfer) {
      delete paymentMethods.bank_transfer;
    }

    if (!newPaymentModalOptions.cashPayment) {
      delete paymentMethods.cash_payment;
    }

    if (!newPaymentModalOptions.creditCard) {
      delete paymentMethods.braspag_credit;
      delete paymentMethods.dlocal;
    }

    if (!newPaymentModalOptions.gpBoleto) {
      delete paymentMethods.gp_boleto;
    }

    if (!newPaymentModalOptions.pix) {
      delete paymentMethods.gp_pix;
    }

    const paymentMethodList = paymentMethods[method];
    const paymentMethodContent = paymentMethodsRules(method, paymentMethodList) || <></>;
    const paymentMethodLocale = paymentModalLocale.paymentMethods[method];

    if (!paymentModalLocale.paymentMethods[method]) {
      return;
    }

    if (paymentMethodList && paymentMethodList.length > 1) {
      return {
        name: !!paymentMethodLocale && (paymentMethodLocale.name || ''),
        icon: !!paymentMethodLocale && (paymentMethodLocale.icon || 'IconData'),
        content: paymentMethodContent,
        key: method,
        options: paymentMethodList,
      };
    }

    if (paymentMethodList && paymentMethodList.length === 1) {
      let methodIcon = null;

      if (paymentModalLocale.paymentMethods[paymentMethodList[0].name.toLowerCase()]) {
        methodIcon = paymentModalLocale.paymentMethods[paymentMethodList[0].name.toLowerCase()].icon;
      }

      if (!methodIcon) {
        methodIcon = paymentModalLocale.paymentMethods[method].icon
        || 'IconData';
      }

      return {
        ...paymentMethodList[0],
        name: !!paymentMethodLocale && (paymentMethodLocale.name || ''),
        icon: methodIcon,
        content: paymentMethodContent,
        key: method,
      };
    }
  };

  const paymentMethodRules = () => {
    const objectToArray = paymentMethods ? Object.entries(paymentMethods).reverse() : [];
    const formatedPaymentMethods = paymentMethods
      ? objectToArray.map(([key]) => mountPaymentMethodObject(key))
      : [];

    const ccMethodIndex = formatedPaymentMethods.findIndex(method => method && method.key && isCcMethod(method.key));

    const moveItemInArray = (fromIndex, toIndex) => {
      const item = formatedPaymentMethods.splice(fromIndex, 1)[0];
      formatedPaymentMethods.splice(toIndex, 0, item);
      return formatedPaymentMethods;
    };

    const currentCcCustomMethod = {
      name: paymentModalLocale.paymentMethods[PaymentMethodsTypes.CURRENT_CREDIT_CARD].name,
      icon: 'IconCard',
      content: paymentMethodsRules(PaymentMethodsTypes.CURRENT_CREDIT_CARD),
      key: 'current_credit_card',
    };

    if (ccMethodIndex > -1) {
      moveItemInArray(ccMethodIndex, 0);

      if (existsCreditCard && !hideCurrentCcMethod) {
        formatedPaymentMethods.splice(0, 0, currentCcCustomMethod);
      }
    }

    return formatedPaymentMethods;
  };

  const formatedPaymentMethods = paymentMethodRules();

  const backToInvoicesList = () => {
    handleClose();
    history.push(routesLocale.invoices);
    window.location.reload();
  };

  const handleClickPageNotLoaded = () => {
    window.open(lastPaymentMethodExternalUrls, '_blank', 'popup');
  };

  const paymentStatusRules = {
    [PaymentStatusTypes.SUCCESS_GENERIC]: {
      ...paymentModalLocale.paymentStatus[PaymentStatusTypes.SUCCESS_GENERIC],
      icon: <IconCheckBg secondaryColor="activePure" size="xl" />,
      buttonProps: {
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.SUCCESS_GENERIC].buttonLabel,
        size: 'large',
        onClick: backToInvoicesList,
      },
    },
    [PaymentStatusTypes.FAILURE_CREDIT_CARD]: {
      ...paymentModalLocale.paymentStatus[PaymentStatusTypes.FAILURE_CREDIT_CARD],
      icon: <IconCardRefresh secondaryColor="problemLight" size="xl" />,
      buttonProps: {
        variant: 'secondary',
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.FAILURE_CREDIT_CARD].buttonLabel,
        loading: isCcButtonLoading,
        disabled: isCcButtonLoading,
        onClick: sendCurrentCreditCard,
        size: 'large',
      },
      secondButtonProps: {
        variant: 'secondary',
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.FAILURE_CREDIT_CARD].secondButtonLabel,
        disabled: isCcButtonLoading,
        onClick: () => setPaymentStatus(null),
        size: 'large',
      },
    },
    [PaymentStatusTypes.FOLLOW_NEW_TAB_INSTRUCTIONS]: {
      icon: <IconExternalLink size="xl" />,
      ...paymentModalLocale.paymentStatus[PaymentStatusTypes.FOLLOW_NEW_TAB_INSTRUCTIONS],
      pageNotLoaded: paymentModalLocale.paymentStatus[PaymentStatusTypes.FOLLOW_NEW_TAB_INSTRUCTIONS].pageNotLoaded({
        handleClick: handleClickPageNotLoaded,
      }),
      buttonProps: {
        variant: 'primary',
        size: 'medium2',
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.FOLLOW_NEW_TAB_INSTRUCTIONS].buttonLabel,
        onClick: handleClose,
      },
    },
    [PaymentStatusTypes.PIX_TIME_EXPIRED]: {
      ...paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_TIME_EXPIRED],
      icon: <IconTimeIllustrated size="xl" />,
      buttonProps: {
        variant: 'text',
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_TIME_EXPIRED].buttonLabel,
        size: 'large',
        onClick: () => setPaymentStatus(null),
      },
      secondButtonProps: {
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_TIME_EXPIRED].secondButtonLabel,
        size: 'large',
        onClick: () => {
          const isExpiredButton = true;
          handleRequestPixQrCode(isExpiredButton);
        },
      },
    },
    [PaymentStatusTypes.GENERIC_FAILURE]: {
      ...paymentModalLocale.paymentStatus[PaymentStatusTypes.GENERIC_FAILURE],
      icon: <IconAtencion size="xl" secondaryColor="problemPure" />,
      buttonProps: {
        variant: 'secondary',
        label: loadingInvoiceData ? <IconLoading /> : paymentModalLocale.paymentStatus[PaymentStatusTypes.GENERIC_FAILURE].tryAgain,
        size: 'large',
        disabled: loadingInvoiceData,
        onClick: () => {
          setLoadingInvoiceData(true);
          handleRequestPixQrCode();
        },
      },
      secondButtonProps: {
        variant: 'secondary',
        label: paymentModalLocale.paymentStatus[PaymentStatusTypes.GENERIC_FAILURE].anotherMethod,
        size: 'large',
        disabled: loadingInvoiceData,
        onClick: () => setPaymentStatus(null),
      },
    },
  };

  const invoiceInfosProps = () => ({
    mobile,
    showInvoiceDetails,
    invoiceDetailsLocale: paymentModalLocale.invoiceDetails,
    invoiceDetailsState: invoicesState.invoiceDetails,
    invoice,
    handleSeeDetails,
  });

  const invoiceDetailsProps = () => ({
    mobile,
    invoiceInfosProps,
    invoice,
    invoiceDetailsData: invoicesState.invoiceDetails && invoicesState.invoiceDetails.data,
    invoiceDetailsLocale: paymentModalLocale.invoiceDetails,
  });

  const saveTicketAsPDF = () => {
    if (ticketData.pdf_url) {
      const link = document.createElement('a');
      link.href = ticketData.pdf_url;
      link.download = ticketData.pdf_url;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      handleClose();
      setTicketData(null);
    }
  };

  const printTicket = () => {
    if (!invoice) {
      return null;
    }
    setLoadingPrintTicket(true);
    dispatch(checkBoletoUserData(invoice.id)).then((response) => {
      if (!response || response.data === undefined || !response.data.success) {
        setLoadingPrintTicket(false);
        return;
      }
      goToTicketPrint();
      setLoadingPrintTicket(false);
    });
  };

  const getQrCodePixCardProps = useCallback(() => {
    const requestInvoiceStatus = () => {
      dispatch(invoicesActions.invoices.loadDetailsV2.request({ invoiceId: invoice.id }));
    };

    const handleSuccessPayment = () => {
      clearPix();
      setPaymentStatus(PaymentStatusTypes.SUCCESS_GENERIC);
    };

    const handleFnishTimer = () => {
      clearPix();
      setPaymentStatus(PaymentStatusTypes.PIX_TIME_EXPIRED);
    };

    return ({
      qrCode: invoicesState.qrCode,
      code: invoicesState.pixCode,
      limitTimeInSecondsToDisplayPixCode,
      onPaymentSuccessPix: handleSuccessPayment,
      requestInvoiceStatus,
      loading: invoicesState.invoiceDetails.loading,
      invoiceDetails: invoicesState.invoiceDetails,
      handleFnishTimer,
      title: paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_ONGOING].contentTitle,
      copyTimerText: paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_ONGOING].copyTimerText,
      list: paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_ONGOING].list,
    });
  }, [
    invoicesState,
    limitTimeInSecondsToDisplayPixCode,
    invoice,
    dispatch,
    clearPix,
    paymentModalLocale,
  ]);

  const paymentModal = ({ onToggleUpdatedInvoices, onExecuteSplitAction } = {}) => {
    const shouldRenderInvoiceInfo = (!mobile || !showInvoiceDetails) && calledFromInvoices;
    const isSuccessPaymentStatus = paymentStatus === PaymentStatusTypes.SUCCESS_GENERIC;
    const handleCloseStatus = isSuccessPaymentStatus ? backToInvoicesList : handleClose;
    const newCreditCardKey = isBRServer ? PaymentMethodsTypes.BRASPAG_CREDIT : PaymentMethodsTypes.DLOCAL;
    const defaultPaymentMethod = existsCreditCard && !hideCurrentCcMethod ? PaymentMethodsTypes.CURRENT_CREDIT_CARD : newCreditCardKey;

    const collapseIsChecked = (methodKey) => {
      if (!selectedMethodKey) {
        return methodKey === defaultPaymentMethod;
      }
      return methodKey === selectedMethodKey;
    };

    const handleGoBack = () => {
      dispatch(invoicesActions.invoices.setInvoiceData({
        data: null,
        loading: false,
        status: null,
      }));

      clearPix();
      setShowInvoiceDetails(false);
      setPaymentStatus(null);
    };

    const getSplitPaymentModalProps = () => {
      const onCloseClick = () => {
        handleClose();
        setPaymentSplited(false);
      };

      const onExecutePayment = (hasSplitedInvoice = false) => {
        setPaymentSplited(true);
        if (hasSplitedInvoice) {
          handleClose();
          onExecuteSplitAction && onExecuteSplitAction();
          onToggleUpdatedInvoices && onToggleUpdatedInvoices(hasSplitedInvoice);
        }
      };

      return {
        onCloseClick,
        onExecutePayment,
        invoice,
      };
    };

    if (displaySplitInvoice) {
      return (
        <SplitPaymentModal {...getSplitPaymentModalProps()} />
      );
    }

    if (paymentStatus === PaymentStatusTypes.PIX_ONGOING) {
      return (
        <Modal
          maxWidth={800}
          maxHeight={800}
          onGoBack={handleGoBack}
          title={paymentModalLocale.paymentStatus[PaymentStatusTypes.PIX_ONGOING].modalTitle}
        >
          <PixContent {...getQrCodePixCardProps()} />
        </Modal>
      );
    }

    if (paymentStatus) {
      return (
        <Modal onClose={handleCloseStatus} maxWidth={800}>
          <PaymentStatus {...paymentStatusRules[paymentStatus]} />
        </Modal>
      );
    }

    if (ticketData) {
      return (
        <TicketDialog
          onGoBack={() => setTicketData(null)}
          digitableLine={ticketData.digitable_line}
          barCode={ticketData.barcode}
          saveTicketAsPDF={saveTicketAsPDF}
          printTicket={printTicket}
          labelsLocale={paymentModalLocale.paymentMethods[PaymentMethodsTypes.GP_BOLETO]}
          isLoading={loadingPrintTicket}
        />
      );
    }

    if (canDisplayInvoiceDetails && showInvoiceDetails && mobile) {
      return (
        <Modal
          title={paymentModalLocale.invoiceDetailsLabel}
          mobileTitleInNewLine
          onGoBack={() => {
            setShowInvoiceDetails(false);
          }}
        >
          <Styles.MobileDetailsContainer>
            <InvoiceDetails {...invoiceDetailsProps()} />
          </Styles.MobileDetailsContainer>
        </Modal>
      );
    }

    if ((!invoice && calledFromInvoices) || loadingPaymentMethods) {
      return (
        <Modal
          maxWidth={800}
          onClose={handleClose}
          title={calledFromInvoices
            ? paymentModalLocale.title
            : paymentModalLocale.titleSubscriptions
          }
        >
          <Styles.InfosLoadingContainer>
            {Array.from({ length: 3 }).map(() => (
              <Skeleton variant="rectangle" width="92px" height="36px" />
            ))}
          </Styles.InfosLoadingContainer>
          <Skeleton variant="headline" width="300px" />
          <Styles.PaymentMethodsWrapper>
            {Array.from({ length: 5 }).map(() => (
              <Skeleton variant="rectangle" height="62px" />
            ))}
          </Styles.PaymentMethodsWrapper>
        </Modal>
      );
    }

    return (
      <Modal
        onClose={handleClose}
        maxWidth={800}
        title={calledFromInvoices
          ? paymentModalLocale.title
          : paymentModalLocale.titleSubscriptions
        }
      >
        <Styles.Wrapper>
          {calledFromSubscriptions && (
            <Styles.Description>
              {paymentModalLocale.descriptionSubscriptions}
            </Styles.Description>
          )}

          {shouldRenderInvoiceInfo && <InvoiceInfos {...invoiceInfosProps()} />}
          {showInvoiceDetails && canDisplayInvoiceDetails && <InvoiceDetails {...invoiceDetailsProps()} />}

          {calledFromInvoices && (
            <Styles.SelectPaymentLabel>
              {paymentModalLocale.selectPaymentMethod}
            </Styles.SelectPaymentLabel>
          )}

          <Styles.PaymentMethodsWrapper>
            {formatedPaymentMethods.map(method => (
              method && method.content && (
                <Styles.RadioLabel
                  key={method.key}
                  onClick={() => handleSelectPaymentMethod({
                    key: method.key,
                    options: method.options,
                    method,
                  })}
                >
                  <RadioCollapse
                    testId={method.key}
                    title={method.name}
                    icon={method.icon}
                    checked={collapseIsChecked(method.key)}
                  >
                    {method.content}
                  </RadioCollapse>
                </Styles.RadioLabel>
              )))}
          </Styles.PaymentMethodsWrapper>
        </Styles.Wrapper>
      </Modal>
    );
  };

  return {
    paymentModal,
  };
};

usePaymentModal.propTypes = {
  onClose: PropTypes.func,
  caller: PropTypes.string,
  handleUpgradeSitelock: PropTypes.func,
};

export default usePaymentModal;
